import React from "react"

import { Edit, ExternalLink } from "lucide-react"

import { TableEmptyRow } from "@/components/shared/EmptyState"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { StatusPill } from "@/components/ui/status-pill"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { TabsContent } from "@/components/ui/tabs"
import { getSlotTypeBadgeClasses } from "@/lib/slotTypeDisplay"
import { cn } from "@/lib/utils"
import { Location, Slot } from "@/types/collections"

import { getSlotServiceColor } from "./badgeColors"

interface SlotsTabProps {
  /** Already narrowed by the directory search. */
  slots: Slot[]
  locations: Location[]
  onOpenSite: (location: Location) => void
}

const SlotsTab: React.FC<SlotsTabProps> = ({ slots, locations, onOpenSite }) => (
  <TabsContent value="slots" className="m-0 h-full">
    <div className="bg-white rounded-lg border border-palette-jungleGreen200">
      <div className="flex items-center justify-between p-4 border-b border-palette-jungleGreen200">
        <h3 className="font-medium text-palette-jungleGreen900">All Slots</h3>
      </div>
      <Table>
        <TableHeader>
          <TableRow className="bg-palette-jungleGreen000/50">
            <TableHead className="text-xs">Slot ID</TableHead>
            <TableHead className="text-xs">Location</TableHead>
            <TableHead className="text-xs">Time Window</TableHead>
            <TableHead className="text-xs">Service Time</TableHead>
            <TableHead className="text-xs">Days</TableHead>
            <TableHead className="text-xs">Type</TableHead>
            <TableHead className="text-xs">Service</TableHead>
            <TableHead className="text-xs text-right">P&L</TableHead>
            <TableHead className="text-xs text-center">Status</TableHead>
            <TableHead className="text-xs text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {slots.length === 0 ? (
            <TableEmptyRow colSpan={10}>No slots found</TableEmptyRow>
          ) : (
            slots.map((slot) => {
              const location = locations.find((l) => l.id === slot.location_id)
              return (
                <TableRow key={slot.id} className="hover:bg-palette-jungleGreen000">
                  <TableCell className="font-medium text-sm font-mono">
                    {slot.slot_name || `${slot.start_time}-${slot.end_time}`}
                  </TableCell>
                  <TableCell className="text-sm">
                    {location ? (
                      <button
                        type="button"
                        onClick={() => onOpenSite(location)}
                        title={`Open ${location.site_name} and its collections`}
                        className="text-blue-600 hover:underline cursor-pointer flex items-center gap-1"
                      >
                        {location.site_name}
                        <ExternalLink size={10} />
                      </button>
                    ) : (
                      <span className="text-palette-jungleGreen400 italic">Unknown</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-palette-jungleGreen600">
                    {slot.start_time} - {slot.end_time}
                  </TableCell>
                  <TableCell className="text-sm text-palette-jungleGreen600">
                    {slot.service_time_minutes} min
                  </TableCell>
                  <TableCell className="text-sm">
                    {Array.isArray(slot.days) && slot.days.length > 0
                      ? slot.days
                          .map((d) => d.charAt(0).toUpperCase() + d.slice(1))
                          .join(", ")
                      : "—"}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="secondary"
                      className={cn(
                        "text-xs pointer-events-none",
                        getSlotTypeBadgeClasses(slot.slot_type),
                      )}
                    >
                      {slot.slot_type
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (l) => l.toUpperCase())}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {Array.isArray(slot.slot_services) &&
                    slot.slot_services.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {slot.slot_services.map((service, idx) => (
                          <Badge
                            key={idx}
                            variant="secondary"
                            className={cn(
                              "text-xs pointer-events-none",
                              getSlotServiceColor(service),
                            )}
                          >
                            {service}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-right">
                    {slot.slot_pnl &&
                    Array.isArray(slot.slot_pnl) &&
                    slot.slot_pnl.length > 0
                      ? slot.slot_pnl.join(", ")
                      : "—"}
                  </TableCell>
                  <TableCell className="text-center">
                    <StatusPill
                      tone={slot.is_active ? "success" : "neutral"}
                      label={slot.is_active ? "Active" : "Inactive"}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    {location && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onOpenSite(location)}
                        title={`Edit ${location.site_name}'s collections`}
                      >
                        <Edit size={14} />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              )
            })
          )}
        </TableBody>
      </Table>
    </div>
  </TabsContent>
)

export default SlotsTab
